import { useState } from "react";
import { usePreflightInviteToken } from "../../Registration/hooks/usePreflightInviteToken";
import { EmailConfirmationStep } from "../../Registration/EmailConfirmationStep";
import { VerificationCodeStep } from "../../Registration/VerificationCodeStep";
import { InviteErrorPage, type InviteErrorCode } from "../../Registration/InviteErrorPage";
import type { ReferralToken } from "../Login/types/ReferralToken.types";
import GuestSignup from "./GuestSignup";
import { RegistrationPrefill } from "../../types/userInvite";

interface RfxPanelInviteSignupProps {
	referralToken: ReferralToken;
}

type Step = "CONFIRM_EMAIL" | "VERIFY_CODE" | "SIGNUP";

/**
 * Read the RFx-guest verify-code entry token from the URL. This is a
 * SEPARATE query param from `rit`/`token_type` (which identify the RFx +
 * invite type) — `invite_token` is the opaque OTP-entry token PRO-637 Task
 * 015 appends to the panel-invitation email link.
 */
const readInviteToken = (): string | null =>
	new URLSearchParams(window.location.search).get("invite_token");

/**
 * RFx panel-invite "Create Account" path (PRO-637 Task 012), shown instead of
 * the plain `GuestSignup` form for `ReferralTokenType.EvaluationPanelMember`.
 * Inserts the mandatory verify-code (OTP) challenge Task 015 added for RFx
 * invite types BEFORE the account-creation form — reusing the existing
 * generic `auth/invite/preflight-link` → `auth/invite/verify-link` →
 * `auth/invite/verify-code` machinery (`usePreflightInviteToken`,
 * `EmailConfirmationStep`, `VerificationCodeStep`) that `RegistrationPage`
 * already uses for the (unrelated) company-user invite flow — Task 015
 * deliberately keyed its RFx guest OTP off this SAME infrastructure so it
 * could be reused here rather than forking a parallel mechanism.
 *
 * On successful verification, renders the existing `GuestSignup` form with
 * the resulting `registration_token` attached — `GuestUserController::
 * guestRegistration` (`POST /api/v1/guest/register`) requires it for RFx
 * invite types (Task 015 gate) and will 403
 * `guest_registration_otp_required` without it.
 */
export const RfxPanelInviteSignup = ({
	referralToken,
}: RfxPanelInviteSignupProps) => {
	// Captured once per mount via lazy initializer (not module-eval time) —
	// we deliberately do NOT re-read/strip it on refresh/re-render (mirrors
	// `RegistrationPage`'s `readToken` — see that file's comment for why),
	// but per-instance capture keeps this testable with plain `render()`
	// calls instead of forcing a `jest.resetModules()` re-require per test.
	const [initialInviteToken] = useState<string | null>(readInviteToken);
	const [step, setStep] = useState<Step>("CONFIRM_EMAIL");
	const [verificationExpiresAt, setVerificationExpiresAt] = useState<
		string | null
	>(null);
	const [registrationToken, setRegistrationToken] = useState<string | null>(
		null
	);
	const [prefill, setPrefill] = useState<RegistrationPrefill | null>(null);
	const [terminalError, setTerminalError] = useState<InviteErrorCode | null>(
		null
	);

	const {
		maskedEmail,
		invitedByName,
		invitedByEmail,
		isLoading,
		errorCode,
		retry: retryPreflight,
	} = usePreflightInviteToken(initialInviteToken);

	const handleCodeSent = (expiresAt: string | null) => {
		setVerificationExpiresAt(expiresAt);
		setStep("VERIFY_CODE");
	};

	const handleCodeVerified = (token: string, prefill: RegistrationPrefill) => {
		setRegistrationToken(token);
		setPrefill(prefill);
		setStep("SIGNUP");
	};

	// No `invite_token` on the link at all (legacy pre-Task-015 invite, or an
	// invitee with no pending enrolment) — there is no OTP flow to run, and
	// `guest/register` will hard-require `registration_token` for this invite
	// type regardless, so this is a terminal state, not a silent fallback.
	// `useAuthWrapper={false}`: Login.tsx already renders this component inside
	// an <AuthContainer>, so letting InviteErrorPage apply its own default
	// wrapper nests one AuthContainer inside another.
	if (!initialInviteToken) {
		return <InviteErrorPage code="INVITE_TOKEN_INVALID" useAuthWrapper={false} />;
	}

	// The preflight-link endpoint DOES legitimately distinguish an expired
	// token (410 INVITE_TOKEN_EXPIRED) from an invalid one (404
	// INVITE_TOKEN_INVALID) — see `UserVerificationController::preflightLink`.
	// Key off `errorCode` directly here rather than the hook's coarse
	// `isExpired` flag: that flag is deliberately collapsed (true for BOTH
	// codes) to keep the generic `RegistrationPage` company-user flow
	// enumeration-safe, which doesn't apply to this RFx guest surface.
	if (errorCode === "INVITE_TOKEN_EXPIRED" || errorCode === "INVITE_TOKEN_INVALID") {
		return <InviteErrorPage code={errorCode} useAuthWrapper={false} />;
	}

	const activeErrorCode = errorCode ?? terminalError;

	if (activeErrorCode) {
		const onRetry =
			activeErrorCode === "PREFLIGHT_FAILED" && !terminalError
				? retryPreflight
				: undefined;
		return <InviteErrorPage code={activeErrorCode} onRetry={onRetry} useAuthWrapper={false} />;
	}

	if (isLoading) {
		return null;
	}

	if (step === "SIGNUP" && registrationToken) {
		return (
			<GuestSignup
				prefill={prefill}
				referralToken={referralToken}
				registrationToken={registrationToken}
				wrapInAuthContainer={false}
			/>
		);
	}

	if (step === "VERIFY_CODE" && maskedEmail) {
		return (
			<VerificationCodeStep
				inviteToken={initialInviteToken}
				maskedEmail={maskedEmail}
				verificationExpiresAt={verificationExpiresAt}
				onSuccess={handleCodeVerified}
			/>
		);
	}

	if (maskedEmail) {
		return (
			<EmailConfirmationStep
				inviteToken={initialInviteToken}
				maskedEmail={maskedEmail}
				invitedByEmail={invitedByEmail}
				invitedByName={invitedByName}
				onCodeSent={handleCodeSent}
				onTerminalError={(code) => setTerminalError(code as InviteErrorCode)}
			/>
		);
	}

	return null;
};
